import { Col } from '@/components';
import { useAnalysisPageSearchQueryStates } from '@/features/analysis/_hooks/search-query-states';
import { GovernmentSearchField } from '@/features/government-search/_components/GovernmentSearchField';
import { fetchGovernmentNumber } from '@/features/government-search/_utils/search-government';
import { useProjectsPageSearchQueryStates } from '@/features/project/_hooks/search-projects/search-query-states';
import type { AnalysisSearchFormType } from '@/features/search/_models/analysis-search';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import type { SharedSearchFormType } from '@/features/search/_models/shared-search';
import { useProjectsSearchStore } from '@/features/search/_stores/projects-search-store';
import { COL_MIN_WIDTH } from '@/features/search/_utils/projects-search';
import { type FC, memo, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Props = {
  resetCount: number;
  onResetPayment: () => void;
};

export const PaymentColField: FC<Props> = memo(
  ({ resetCount, onResetPayment }) => {
    const { t } = useTranslation();
    const { type } = useProjectsSearchStore();

    const {
      control: sharedSearchControl,
      formState: { errors },
      setValue: setSharedSearchValue,
    } = useFormContext<SharedSearchFormType>();
    const { control: projectSearchControl } =
      useFormContext<ProjectSearchFormType>();
    const { control: analysisSearchControl } =
      useFormContext<AnalysisSearchFormType>();
    const {
      searchQueryStates: { corporateNumber },
    } =
      type === 'analysis'
        ? useAnalysisPageSearchQueryStates()
        : useProjectsPageSearchQueryStates();
    const isFirstRender = useRef(true);
    const prevValue = useRef<string | undefined>(undefined);
    const [defaultSearchValue, setDefaultSearchValue] = useState<
      string | undefined
    >(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const prevResetCount = useRef(0);
    const [isResetProcessing, setIsResetProcessing] = useState(false);

    useEffect(() => {
      // booleanだと一瞬の変化で検知できないため数値で比較
      if (prevResetCount.current < resetCount) {
        prevResetCount.current = resetCount;
        setIsResetProcessing(true);
        // 一瞬すぎると値の更新を検知できないため、100ms後にfalseにする
        setTimeout(() => {
          setIsResetProcessing(false);
        }, 100);
      }
    }, [resetCount]);

    useEffect(() => {
      if (corporateNumber && isFirstRender.current) {
        (async () => {
          const res = await fetchGovernmentNumber({ corporateNumber });
          setDefaultSearchValue(res.at(0)?.corporateName);
          setIsLoading(false);
        })();
      } else {
        setIsLoading(false);
      }
      isFirstRender.current = false;
    }, [corporateNumber]);

    const sheetType = useWatch({
      control: sharedSearchControl,
      name: 'sheetType',
    });

    const watchedValues = useWatch({
      control: projectSearchControl,
      name: ['fundSheetType', 'skipAdministrativeReformTeamEvaluation'],
    });

    const analysisTarget = useWatch({
      control: analysisSearchControl,
      name: 'analysisTarget',
    });

    const [fundSheetType, skipAdministrativeReformTeamEvaluation] =
      watchedValues;

    const isNotStandardFundSheetType =
      sheetType === 'KS' && fundSheetType !== 'standard';
    const isSkipAdministrativeReformTeamEvaluation =
      sheetType === 'RS' && skipAdministrativeReformTeamEvaluation === 'true';

    const noDisplay =
      isNotStandardFundSheetType || isSkipAdministrativeReformTeamEvaluation;

    useEffect(() => {
      if (noDisplay) {
        onResetPayment();
      }
    }, [noDisplay, onResetPayment]);

    return (
      <Col
        span={8}
        style={{
          minWidth: COL_MIN_WIDTH.large,
          // return nullだとnoDisplayの時のフォームの値がリセットされるまえにDOMが切り替えられ、再表示した際にフォームの値がリセットされていない。そのためdisplay: 'none'を使用する。
          display: noDisplay ? 'none' : undefined,
        }}
      >
        {!isLoading && (
          <GovernmentSearchField
            name="corporateNumber"
            label={t('payment-name')}
            control={sharedSearchControl}
            errors={errors}
            isResetProcessing={isResetProcessing}
            defaultSearchValue={defaultSearchValue}
            required={analysisTarget === 'payment'}
            onSelect={({ corporateInfo }) => {
              setSharedSearchValue(
                'corporateNumber',
                corporateInfo?.corporateNumber ?? null,
              );
            }}
            companyNameHint={t('hint-payment-name-selection')}
            companyNumberHint={t(
              'hint-payment-name-selection-by-company-number',
            )}
            onChange={(value) => {
              if (value !== prevValue.current) {
                setSharedSearchValue('corporateNumber', null);
              }
              prevValue.current = value;
            }}
          />
        )}
      </Col>
    );
  },
);
